import { Box, Button, MenuItem, TextField } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import type { JSX } from "react";

/**
 * Bottom pagination strip for the leads list. Three columns: a "1–50 of 247
 * leads" range on the left, a page indicator + jump-to selector in the centre,
 * and Prev / Next on the right. Extracted from `LeadsPage.tsx`.
 */
export function LeadsPagination({
  total,
  page,
  perPage,
  totalPages,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
  onJump,
}: {
  total: number;
  page: number;
  perPage: number;
  totalPages: number;
  hasPrev: boolean;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  onJump: (page: number) => void;
}): JSX.Element {
  const start = (page - 1) * perPage + 1;
  const end = Math.min(page * perPage, total);

  return (
    <Box
      component="nav"
      aria-label="Lead pagination"
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        p: 1.5,
        fontSize: 12,
      }}
    >
      <Box component="span" sx={{ color: "text.disabled" }}>
        {start}–{end} of {total.toLocaleString()} lead{total === 1 ? "" : "s"}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.disabled" }}>
        <Box component="span">Page</Box>
        <TextField
          select
          value={page}
          onChange={(e) => onJump(Number(e.target.value))}
          aria-label="Jump to page"
          sx={{ "& .MuiSelect-select": { py: 0.5, fontSize: 12 } }}
        >
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <MenuItem key={p} value={p}>
              {p}
            </MenuItem>
          ))}
        </TextField>
        <Box component="span">of {totalPages}</Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <PageButton disabled={!hasPrev} onClick={onPrev}>
          ← Previous
        </PageButton>
        <PageButton disabled={!hasNext} onClick={onNext}>
          Next →
        </PageButton>
      </Box>
    </Box>
  );
}

function PageButton({
  disabled,
  onClick,
  children,
}: {
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <Button
      type="button"
      variant="outlined"
      color="inherit"
      size="small"
      disabled={disabled}
      onClick={onClick}
      sx={
        {
          minWidth: 0,
          px: 1.5,
          py: 0.5,
          fontSize: 12,
          fontWeight: 500,
          color: "text.primary",
          "&:hover": { bgcolor: "surfaceSoft" },
          "&.Mui-disabled": { opacity: 0.4 },
        } as SxProps<Theme>
      }
    >
      {children}
    </Button>
  );
}
